import html2canvas from 'html2canvas';
import { RefObject } from 'react';

/**
 * Method to generate a name of file to be downloaded in form of
 * "chartType_sprintName_sprintEnddate"
 * @param chartType string
 * @param sprintName string
 * @param endDate datetime string
 * @returns name string of the file to be downloaded
 */
const generateDownloadFileName = (chartType: string, sprintName: string, endDate: string) => {
  const enddate = new Date(endDate);

  const month = String(enddate.getUTCMonth() + 1).padStart(2, '0'); // Add leading zero to month if it is a single digit
  const day = String(enddate.getUTCDate()).padStart(2, '0'); // Add leading zero to day if it is a single digit

  const fileName = `${chartType}_${sprintName}_${month}_${day}`;
  return fileName;
};

/**
 * Method to facilitate the download of an html element as an image file, with an optional element to hide
 * @param downloadFilename <string> name of file to be downloaded
 * @param downloadRef <ref object>
 * @param exceptionRef <ref object> (optional)
 */
const canvasHtmlDownload = (
  downloadFilename: string,
  downloadRef: RefObject<HTMLDivElement>,
  exceptionRef?: RefObject<HTMLDivElement>,
) => {
  const downloadElement = downloadRef.current as HTMLDivElement;
  let initialExceptionElementDisplayProperty: string | null = null;

  // Only handle the exception element if the ref is provided
  if (exceptionRef && exceptionRef.current) {
    const exceptionElement = exceptionRef.current;
    initialExceptionElementDisplayProperty = exceptionElement.style.display;
    exceptionElement.style.display = 'none';
  }

  html2canvas(downloadElement, {
    scale: 2, // Higher quality
    logging: false,
    useCORS: true,
    backgroundColor: '#ffffff', // Ensure white background to prevent transparency issues
  }).then((canvas) => {
    const link = document.createElement('a');
    link.download = `${downloadFilename}.png`;
    link.href = canvas.toDataURL();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Restore the exception element's display property if it was modified
    if (exceptionRef && exceptionRef.current && initialExceptionElementDisplayProperty !== null) {
      exceptionRef.current.style.display = initialExceptionElementDisplayProperty;
    }
  });
};

/**
 * Exports an HTML element as a PNG image file
 * @param element - The HTML element to export as an image
 * @param filename - The name of the downloaded file (default: 'chart-export.png')
 * @returns A Promise that resolves when the export is complete or rejects with an error
 */
const exportChart = (element: HTMLElement | null, filename = 'chart-export.png'): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!element) {
      reject(new Error('No element provided for export'));
      return;
    }

    html2canvas(element, {
      scale: 2, // Higher quality
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff', // Ensure white background to prevent transparency issues
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imgData;
        link.download = filename;
        link.click();
        resolve();
      })
      .catch((err) => {
        console.error('Error generating chart image:', err);
        reject(err);
      });
  });
};

export { canvasHtmlDownload, exportChart, generateDownloadFileName };
